import { Component, effect, Inject, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ApiEndPointConstants, HelperService } from 'merlin-core';
import {
    UserService,
    RoleObject,
    SpinnerService,
    UserProfileObject,
    Role,
} from 'merlin-library';
import { MyAccountService } from 'projects/clinician/src/app/my-account/services/my-account.service';

import {
    MsalBroadcastService,
    MsalGuardConfiguration,
    MsalService,
    MSAL_GUARD_CONFIG,
} from '@azure/msal-angular';
import { Router } from '@angular/router';
import { filter, take, takeUntil, switchMap, map, catchError } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import {
    AuthenticationResult,
    EventMessage,
    EventType,
    InteractionStatus,
    InteractionType,
    PopupRequest,
    RedirectRequest,
} from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import {
    LanguageSource,
    LocalizationService,
} from 'src/core/utilitites/localization.service';
import { ToasterClass } from 'src/core/utilitites/core.constants';
import { EmailLinkService } from '../services/email-link.service';
import { EditMfaRecoveryService } from '../services/edit-mfa-recovery.service';

@Component({
    selector: 'app-oidc-base',
    templateUrl: './oidc-base.component.html',
    styleUrls: ['./oidc-base.component.scss'],
    standalone:false
})
export class OidcBaseComponent implements OnInit {
    claims;
    title = 'merlin-cloud';
    loginDisplay = false;
    isIframe = false;
    roleObject: RoleObject;

    public t: Record<string, string> = {
        "label.gb.title.merlin.pcn" : "Merlin.net\u2122 Patient Care Network",
        "label.gb.loading": "Loading",
    };
    constructor(
        public spinnerSer: SpinnerService,
        @Inject(MSAL_GUARD_CONFIG)
        private msalGuardConfig: MsalGuardConfiguration,
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private router: Router,
        private userService: UserService,
        private myAccountService: MyAccountService,
        private toastrService: ToastrService,
        private helpService: HelperService,
        private localizationService: LocalizationService,
        private emailLinkService: EmailLinkService,
        private editMfaRecovery: EditMfaRecoveryService
    ) {
            effect(() => {
                const lang = this.localizationService.currentLang();
                if (!lang) return;
                this.reparseLocalizedStrings();
            });
        }

    ngOnInit(): void {
        this.isIframe = window !== window.parent && !window.opener;

        // Keep sessionStorage token in sync with MSAL silent renewals
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
                ),
                takeUntil(this.helpService._destroying$)
            )
            .subscribe((result: EventMessage) => {
                const payload = result.payload as AuthenticationResult;
                if (payload?.accessToken) {
                    window.sessionStorage.setItem('accessToken', payload.accessToken);
                }
            });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter(
                    (status: InteractionStatus) =>
                        status === InteractionStatus.None
                ),
                takeUntil(this.helpService._destroying$)
            )
            .subscribe(() => {
                this.getAccessOrLogin();
            });
    }

    setLoginDisplay() {
        this.loginDisplay =
            this.authService.instance.getAllAccounts().length > 0;
    }

    getAccessOrLogin() {
        this.setLoginDisplay();
        if (
            this.loginDisplay &&
            (window.sessionStorage.getItem('accessToken') == null ||
                window.sessionStorage.getItem('accessToken') == undefined)
        ) {
            this.fetchAccessToken();
        } else if (window.sessionStorage.getItem('accessToken')?.length > 0) {
            this.navigateToApplication(false);
        } else {
            this.login();
        }
    }

    login() {
        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            if (this.msalGuardConfig.authRequest) {
                this.authService
                    .loginPopup({
                        ...this.msalGuardConfig.authRequest,
                    } as PopupRequest)
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(
                            response.account
                        );
                    });
            } else {
                this.authService
                    .loginPopup()
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(
                            response.account
                        );
                    });
            }
        } else {
            let params;
            if (window.location.hash === '#imp-success') {
                params = {
                    ...this.msalGuardConfig.authRequest,
                    extraQueryParameters: {
                        impersonatedUrl: window.btoa(environment.impersonate_url),
                    }
                } as RedirectRequest;
            } else {
                params = {
                    ...this.msalGuardConfig.authRequest,
                } as RedirectRequest;
            }
            this.authService.loginRedirect(params);
        }
    }
    navigateToApplication(enableRedirect: boolean = true) {
        this.fetchUserProfileWithPreference().pipe(
            takeUntil(this.helpService._destroying$)
        ).subscribe(
            (userData) => {
                window.location.hash = '';
                this.setRoleBSubject(userData);
                this.redirectToLandingPage(userData, enableRedirect);
            },
            (error) => {
                if (error?.statusCode === 401) {
                    this.goto('unauthorized');
                } else {
                    // Retry with the same profile fetch logic
                    this.fetchUserProfileWithPreference().pipe(
                        takeUntil(this.helpService._destroying$)
                    ).subscribe(
                        (userData) => {
                            window.location.hash = '';
                            this.setRoleBSubject(userData);
                            this.redirectToLandingPage(userData, enableRedirect);
                        },
                        (retryError) => {
                            // Handle retry failure - redirect to unauthorized or show error
                            console.error('Failed to fetch user profile on retry:', retryError);
                            this.goto('unauthorized');
                        }
                    );
                }
            }
        );
    }

    private fetchUserProfileWithPreference(): Observable<UserProfileObject> {
        return this.userService.getUserProfile(false).pipe(
            switchMap((userData: UserProfileObject) => {
                if (!userData.userRecordId) {
                    return of(userData);
                }
                return this.myAccountService.view(userData.userRecordId).pipe(
                    map(accountData => {
                        (userData as any).landingPagePreferenceCd = accountData.landingPagePreferenceCd;
                        return userData;
                    }),
                    catchError(() => of(userData))
                );
            })
        );
    }
    

    setRoleBSubject(userData: UserProfileObject) {
        this.roleObject = this.userService.getRolesAsObject(userData);
        this.userService.setUserRoles(this.roleObject);
    }

    redirectToLandingPage(userData: UserProfileObject, enableRedirect) {
        this.toastrService.clear();
        const locale = userData?.locale;
        this.localizationService.setLanguage(
            LanguageSource.UserDefined,
            locale,
            () => {
                this.reparseLocalizedStrings();
                document.documentElement.style.setProperty(
                    '--toast-ok-label',
                    `"${this.localizationService.generateLocalizedStr('label.gb.ok')}"`
                );
            }
        );
        this.userService.setUserObs(userData);
        this.userService.setRoleObs(Role.User);
        // Cancel from edit-MFA can land on '/' (back-button) instead of '/base'; recover to RP.
        if (this.editMfaRecovery.isEditMfaReturn()) {
            this.editMfaRecovery.recoverToRemoteProgramming();
            return;
        }
        if (window.location.pathname.includes('/base')) {
            setTimeout(() => {
                if (userData.impersonatedUser) {
                    if (userData.activatorClinicFlg) {
                        this.goto('/patient-list');
                    } else {
                        const prefCode = (userData as any)?.landingPagePreferenceCd;
                        const landingRoute = this.myAccountService.getRouteForPreference(prefCode) || ApiEndPointConstants.quickLinkDashboardURL;
                        this.emailLinkService.handleEmailLink(this.checkIsSP2Physician(userData) ? userData?.customerAccountLocationIdList : undefined).subscribe(handled => {
                            if (handled) { return; }
                            if (window.sessionStorage.getItem('emailLinkInvalid')) {
                                window.sessionStorage.removeItem('emailLinkInvalid');
                                this.goto(landingRoute);
                                this.toastrService.warning(
                                    this.localizationService.generateLocalizedStr('msg.lo835.text'),
                                    '',
                                    { disableTimeOut: true, closeButton: true, toastClass: `${ToasterClass.userNotificationMessageC} ${ToasterClass.ngxToastr}` }
                                );
                                return;
                            }
                            this.goto(landingRoute);
                        });
                    }
                } else {
                    if(userData.userMigrationStatus === "MONC"){
                        this.goto(ApiEndPointConstants.oneTimeWizard);
                    }
                    else if (!userData.termsAndCondAcceptanceFlg) {
                        window.sessionStorage.setItem('terms', 'false')
                        this.goto(ApiEndPointConstants.termsRouteUrl);
                    } else {
                        this.emailLinkService.handleEmailLink(this.checkIsSP2Physician(userData) ? userData?.customerAccountLocationIdList : undefined).subscribe(handled => {
                            if (handled) { return; }
                            if (userData.activatorClinicFlg) {
                                this.goto('/patient-list');
                                return;
                            }
                            const prefCode = (userData as any)?.landingPagePreferenceCd;
                            const landingRoute = this.myAccountService.getRouteForPreference(prefCode) || ApiEndPointConstants.quickLinkDashboardURL;
                            if (window.sessionStorage.getItem('emailLinkInvalid')) {
                                window.sessionStorage.removeItem('emailLinkInvalid');
                                this.goto(landingRoute);
                                this.toastrService.warning(
                                    this.localizationService.generateLocalizedStr('msg.lo835.text'),
                                    '',
                                    { disableTimeOut: true, closeButton: true, toastClass: `${ToasterClass.userNotificationMessageC} ${ToasterClass.ngxToastr}` }
                                );
                                return;
                            }
                            this.goto(landingRoute);
                        });
                    }
                }
            }, 1000);
        }
    }
    private checkIsSP2Physician(userData: any): boolean {
        return userData?.customerTypeCd === 32 && userData?.individualTypeCd === 42;
    }

    goto(url: string) {
        this.router.navigateByUrl(url);
    }
    fetchAccessToken() {
        
        if (window.sessionStorage.getItem('accessToken')?.length > 0) {
            this.navigateToApplication(false);
        } else {
            this.authService
                .acquireTokenSilent({
                    scopes: this.generateScopes()
                } as RedirectRequest)
                .pipe(take(1))
                .subscribe(
                    (res: any) => {
                        window.sessionStorage.setItem(
                            'accessToken',
                            res.accessToken
                        );
                        window.sessionStorage.setItem(
                            'guid',
                            uuidv4() + '_'
                        );
                        this.navigateToApplication();
                    },
                    (err) => {
                        this.authService.logoutRedirect({
                            postLogoutRedirectUri: environment.redirectUri,
                        });
                    }
                );
        }
    }
    generateScopes() {
        const returnArray = [];
        environment.scopePermissions.split(' ').forEach((scopeUrl) => {
            returnArray.push(environment.scope + '/' + scopeUrl);
        });
        return returnArray;
    }
    translate(key) {
        return this.localizationService.generateLocalizedStr(key);
    }
    reparseLocalizedStrings(){
        this.t = this.localizationService.reparseLocalizedStrings(this.t);
    }
}

